import { CSSProperties, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area.tsx';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { LastUpdateTimestamp } from '@/components/last-update-timestamp.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { EventsMoreSettingsPopover } from './plant-events-more-options-popover.tsx';
import {
  TPlantEvent,
  useGetPlantEventsQuery,
} from '@/features/plants/hooks/queries/[plantId]/use-get-plant-events.ts';
import { cn } from '@/utils/utils.ts';
import { useDebounce } from '@/hooks/use-debounce.ts';

const EVENT_STYLES: Record<string, CSSProperties> = {
  Info: { backgroundColor: '#93c5fd' },
  Major: { backgroundColor: '#fca5a5' },
};
const DEFAULT_EVENT_STYLE: CSSProperties = { backgroundColor: '#e5e7eb' };
const EMPTY_EVENTS: TPlantEvent[] = [];

export const PlantEvents = ({ className }: { className?: string }) => {
  const { plantId } = useParams();
  const { ref: scrollRef, inView } = useInView();
  const { t } = useTranslation();

  const {
    data: events,
    fetchNextPage,
    isFetchingNextPage,
    dataUpdatedAt,
    isLoading,
    hasNextPage,
  } = useGetPlantEventsQuery({ plantId });

  const debouncedEventsPagesLength = useDebounce(events?.pages.length, 600);

  const columns = useMemo(
    (): ColumnDef<TPlantEvent>[] => [
      {
        id: 'date',
        accessorKey: 'createdAt',
        header: () => t('common.type.date'),
        cell: (info) => {
          const date = info.getValue<string>();
          return date ? format(date, 'dd/MM/yyyy HH:mm') : '';
        },
        size: 200,
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: () => t('common.type.type'),
        cell: (info) => {
          const type = info.getValue<TPlantEvent['type']>();
          return (
            <span
              className="rounded-lg px-2 py-0.5"
              style={EVENT_STYLES[type] ?? DEFAULT_EVENT_STYLE}
            >
              {type}
            </span>
          );
        },
        size: 140,
      },
      {
        id: 'message',
        accessorKey: 'description',
        header: () => t('common.type.message'),
        cell: (info) => info.getValue(),
      },
    ],
    [t]
  );

  const tableData = events?.pages ?? EMPTY_EVENTS;

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [debouncedEventsPagesLength, inView, hasNextPage, fetchNextPage]);

  return (
    <DashboardCard
      isLoading={isLoading}
      className={cn('max-h-[420px] min-h-[200px] gap-1', className)}
    >
      <>
        <div className="flex justify-between">
          <div className="flex w-full flex-col">
            <h3 className="font-semibold text-nowrap">{t('dashboard.events.title')}</h3>
            <LastUpdateTimestamp updatedAt={dataUpdatedAt} />
          </div>
          <EventsMoreSettingsPopover />
        </div>

        <div className="h-[340px]">
          <ScrollArea className="flex h-full w-full">
            <Table>
              <TableHeader className="bg-background sticky top-0 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={cn(
                          header.id === 'date' && 'w-0 sm:w-[200px]',
                          header.id === 'type' && 'w-0 sm:w-[140px]'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length === 0 && (
                  <TableRow className="border-0 pt-4 pl-2">
                    <TableCell colSpan={columns.length}>
                      {t('dashboard.events.no_events')}
                    </TableCell>
                  </TableRow>
                )}
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          'h-[45px]',
                          (cell.column.id === 'date' || cell.column.id === 'type') &&
                            'text-nowrap',
                          cell.column.id === 'message' && 'min-h-[45px]'
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                <TableRow ref={scrollRef} className="h-px border-0 pt-4 pb-2">
                  {((inView && hasNextPage) || isFetchingNextPage) && (
                    <TableCell colSpan={columns.length}>
                      <div className="flex w-full items-center justify-center py-4">
                        <LoadingSpinner className="text-muted-foreground size-10" />
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </div>
      </>
    </DashboardCard>
  );
};
